import React, { ForwardedRef, forwardRef, useContext } from 'react'
import { LinkProps as NextLinkProps } from 'next/link'
import NextLink from 'next/link'
import { Link, LinkProps } from '@grammarly/badger-ui'
import { PageContext } from '../../../contexts/page-context'
import { composeCMSRelativeHref } from '../../../lib/utils/url'

import cn from 'classnames'
import { useIsSectionThemeDark } from '../../../contexts/section-theme-context'
import linkColorStyles from './link-color.module.scss'
import { usePageTheme } from '../../../contexts/page-theme-context'

export const useLinkStyleFromTheme = () => {
  const isDarkThemeSection = useIsSectionThemeDark()
  const { color } = usePageTheme()
  const colorStyles = isDarkThemeSection ? linkColorStyles.linkWhite : linkColorStyles.linkDefault
  const yearStyles = color === 'convertTo2024' ? linkColorStyles.styles2024 : linkColorStyles.styles2023
  return cn(colorStyles, yearStyles)
}

export interface ExternalLinkProps extends Omit<LinkProps, 'ref' | 'onClick'> {
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void | Promise<void>
}

/** Use for external links or links which may be internal or external. For links guaranteed to be a CMS page, use {@link InternalLink} instead. */
export const ExternalLink = forwardRef((props: ExternalLinkProps, ref: ForwardedRef<HTMLAnchorElement>) => {
  const { children, onClick, href, ...rest } = props

  const handleOnClick: React.MouseEventHandler<HTMLAnchorElement> = async (e) => {
    const shouldPreventDefault = onClick && href
    if (shouldPreventDefault) {
      e.preventDefault()
    }
    await onClick?.(e)
    if (shouldPreventDefault) {
      location.href = href
    }
  }

  return (
    <Link {...rest} href={href} onClick={handleOnClick} className={cn(rest.className)} ref={ref}>
      {children}
    </Link>
  )
})

export type InternalLinkProps = Omit<LinkProps, 'ref'> & NextLinkProps

/** Use only for links to CMS pages. Uses client-side navigation. For links that may be external, use {@link ExternalLink} instead. */
export const InternalLink = (props: InternalLinkProps) => {
  const { children, href: plainHref, as, replace, scroll, shallow, prefetch, locale, ...rest } = props
  const { isPreview } = useContext(PageContext)
  const colorStyles = useLinkStyleFromTheme()

  const href = composeCMSRelativeHref(plainHref, isPreview)
  const nextLinkProps = { href, as, replace, scroll, shallow, prefetch, locale }

  return (
    <NextLink {...nextLinkProps} passHref legacyBehavior>
      <Link {...rest} className={cn(colorStyles, rest.className)}>
        {children}
      </Link>
    </NextLink>
  )
}
